import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertCircle, CheckCircle2, RefreshCw, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole } from '@/lib/utils';
import { Navigate } from 'react-router-dom';

/**
 * Diagnoses contract_requests / carrier_appointments rows that reference a
 * carrier_id which no longer exists in the carriers collection (produces
 * "Unknown Carrier" in the UI). Where the bad id looks like a real carrier
 * NAME, offers a one-click heal that rewrites the id.
 */
export default function OrphanCarriersPage() {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [healing, setHealing] = useState(false);
  const [healResult, setHealResult] = useState(null);

  const scan = useCallback(async () => {
    setLoading(true);
    setHealResult(null);
    try {
      const r = await api.get('/admin/orphan-carriers');
      setReport(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Scan failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { scan(); }, [scan]);

  if (!isKRRole(user)) return <Navigate to="/dashboard" replace />;

  const healable = report ? Object.keys(report.heal_map || {}).length : 0;
  const unmappable = report?.unmappable?.length || 0;

  const heal = async () => {
    if (!healable) return;
    if (!window.confirm(
      `Rewrite ${healable} orphan carrier ID(s) using the proposed matches?\n\n`
      + `This updates contract_requests and carrier_appointments rows to point at real carrier records. `
      + `Audit logs are written for every change.`
    )) return;
    setHealing(true);
    try {
      const r = await api.post('/admin/orphan-carriers/heal', { heal_map: report.heal_map });
      setHealResult(r.data);
      toast.success('Heal applied — carrier names should now render correctly.');
      await scan();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Heal failed');
    } finally {
      setHealing(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="orphan-carriers-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Wrench className="w-5 h-5 text-amber-600" />
            <h1 className="text-2xl font-bold">Orphan Carrier Cleanup</h1>
          </div>
          <p className="text-sm text-slate-600 max-w-2xl">
            Finds contract requests and appointments whose <code>carrier_id</code> doesn&apos;t
            match any real carrier — this is what makes carriers show as &ldquo;Unknown&rdquo;
            on the agent profile page. Where the orphan id happens to be a real
            carrier NAME, we can fix it in one click.
          </p>
        </div>
        <Button variant="outline" onClick={scan} disabled={loading} data-testid="orphan-carriers-rescan">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Rescan
        </Button>
      </div>

      {loading ? (
        <div className="p-12 text-center text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      ) : !report ? null : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card><CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Orphan IDs found</CardTitle>
            </CardHeader><CardContent>
              <div className="text-2xl font-bold">{report.total_orphan_ids}</div>
            </CardContent></Card>
            <Card><CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-emerald-600">Healable</CardTitle>
            </CardHeader><CardContent>
              <div className="text-2xl font-bold text-emerald-600">{healable}</div>
            </CardContent></Card>
            <Card><CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-rose-600">Unmappable</CardTitle>
            </CardHeader><CardContent>
              <div className="text-2xl font-bold text-rose-600">{unmappable}</div>
            </CardContent></Card>
          </div>

          {healable > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-700" />
                  Proposed heal — {healable} carrier ID{healable === 1 ? '' : 's'}
                </CardTitle>
                <CardDescription>
                  Review the mapping below. Clicking &ldquo;Apply Heal&rdquo; will rewrite
                  the <code>carrier_id</code> on all affected rows.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="border rounded bg-white divide-y max-h-80 overflow-y-auto">
                  {Object.entries(report.heal_map).map(([badId, target]) => (
                    <div key={badId} className="p-2 text-sm flex items-center gap-2">
                      <Badge variant="outline" className="text-rose-700 border-rose-300">
                        {String(badId).slice(0, 40)}
                      </Badge>
                      <span className="text-slate-400">→</span>
                      <Badge className="bg-emerald-100 text-emerald-800">
                        {target.new_name} <span className="opacity-60 ml-1">({target.new_id.slice(0, 8)}…)</span>
                      </Badge>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={heal}
                    disabled={healing}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="orphan-carriers-heal-btn"
                  >
                    {healing
                      ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Applying…</>
                      : <>Apply Heal ({healable})</>
                    }
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {unmappable > 0 && (
            <Card className="border-rose-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Unmappable orphans</CardTitle>
                <CardDescription>
                  These carrier_id values don&apos;t match any known carrier name.
                  You&apos;ll need to either create the carrier or manually edit the affected rows.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-xs font-mono space-y-0.5 max-h-60 overflow-y-auto">
                {report.unmappable.map((id) => <div key={id}>{id}</div>)}
              </CardContent>
            </Card>
          )}

          {healable === 0 && unmappable === 0 && (
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="py-8 text-center">
                <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-600 mb-2" />
                <p className="text-emerald-900 font-medium">All carrier references are healthy.</p>
              </CardContent>
            </Card>
          )}

          {healResult && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Last heal result</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                {Object.entries(healResult.updates || {}).map(([badId, u]) => (
                  <div key={badId} className="font-mono">
                    {badId.slice(0, 30)}… → {u.new_id.slice(0, 8)}… · <strong>{u.rows_updated}</strong> row{u.rows_updated === 1 ? '' : 's'} updated
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
